import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { academicApi } from '@/services/academic.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { PersonPicker } from '@/modules/people/person-picker';

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/** One section's roster — enroll, end an enrollment (with a final grade),
 *  or correct a grade already on record. "Gestión de Matrículas". */
export function CourseSectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const sectionId = id!;
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const section = useQuery({
    queryKey: ['academic', 'course-sections', 'detail', sectionId],
    queryFn: ({ signal }) => academicApi.getCourseSection(sectionId, signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({
      queryKey: ['academic', 'course-sections', 'detail', sectionId],
    });

  const [person, setPerson] = useState<{ id: string; displayName: string } | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const enroll = useMutation({
    mutationFn: () => academicApi.enroll(sectionId, { personId: person!.id }),
    onSuccess: () => {
      setPerson(null);
      setEnrollError(null);
      invalidate();
    },
    onError: (err: unknown) => setEnrollError(errorMessage(err, t)),
  });

  const canManage = can('academic.enrollment.manage');

  if (section.isPending) return <Spinner label={t('common.states.loading')} />;
  if (section.isError) return <Alert tone="danger">{errorMessage(section.error, t)}</Alert>;

  const data = section.data;

  return (
    <div className="page">
      <Link to="/academic/schools" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{data.subjectName}</h1>
          <p className="page-subtitle">
            {data.term}
            {data.schedule && ` · ${data.schedule}`}
            {data.teacherDisplayName && ` · ${data.teacherDisplayName}`}
          </p>
        </div>
      </header>

      {canManage && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (person) enroll.mutate();
          }}
        >
          {enrollError && <Alert tone="danger">{enrollError}</Alert>}
          {person ? (
            <span className="chip">
              {person.displayName}
              <button type="button" onClick={() => setPerson(null)}>
                ×
              </button>
            </span>
          ) : (
            <PersonPicker
              label={t('academic.enrollment.addStudent')}
              placeholder={t('groups.group.choosePerson')}
              exclude={data.enrollments.map((e) => e.personId)}
              onPick={(id, displayName) => setPerson({ id, displayName })}
            />
          )}
          <Button type="submit" variant="primary" loading={enroll.isPending} disabled={!person}>
            {t('academic.enrollment.enroll')}
          </Button>
        </form>
      )}

      {data.enrollments.length === 0 && <EmptyState title={t('academic.enrollment.empty')} />}

      {data.enrollments.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.person.title')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col">{t('academic.enrollment.grade')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {data.enrollments.map((enrollment) => (
                <EnrollmentRow
                  key={enrollment.personId}
                  sectionId={sectionId}
                  enrollment={enrollment}
                  canManage={canManage}
                  onChanged={invalidate}
                  t={t}
                  locale={locale}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EnrollmentRow({
  sectionId,
  enrollment,
  canManage,
  onChanged,
  t,
}: {
  sectionId: string;
  enrollment: {
    personId: string;
    displayName: string;
    status: string;
    selfEnrolled: boolean;
    finalGrade: number | null;
    letterGrade: string | null;
  };
  canManage: boolean;
  onChanged: () => void;
  t: ReturnType<typeof useI18n>['t'];
  locale: string;
}) {
  const [grade, setGrade] = useState(enrollment.finalGrade?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);

  const complete = useMutation({
    mutationFn: (status: 'COMPLETED' | 'WITHDRAWN') =>
      academicApi.endEnrollment(sectionId, enrollment.personId, {
        status,
        grade: grade ? Number(grade) : null,
      }),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const recordGrade = useMutation({
    mutationFn: () =>
      academicApi.recordGrade(sectionId, enrollment.personId, { grade: Number(grade) }),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const active = enrollment.status === 'ENROLLED';

  return (
    <tr>
      <td>
        {enrollment.displayName}
        {enrollment.selfEnrolled && (
          <span className="cell-flag" title={t('academic.enrollment.selfEnrolled')}>
            {' '}
            · {t('academic.enrollment.selfEnrolledShort')}
          </span>
        )}
      </td>
      <td>{t(`academic.enrollment.status.${enrollment.status}`)}</td>
      <td dir="ltr">
        {canManage ? (
          <input
            className="field-input"
            type="number"
            min={0}
            max={100}
            value={grade}
            onChange={(event) => setGrade(event.target.value)}
            style={{ width: '5rem' }}
          />
        ) : (
          enrollment.finalGrade !== null && `${enrollment.finalGrade} (${enrollment.letterGrade})`
        )}
      </td>
      <td>
        {error && <Alert tone="danger">{error}</Alert>}
        {canManage && (
          <>
            {!active && grade && (
              <Button
                variant="ghost"
                loading={recordGrade.isPending}
                onClick={() => recordGrade.mutate()}
              >
                {t('academic.enrollment.saveGrade')}
              </Button>
            )}
            {active && (
              <>
                <Button
                  variant="ghost"
                  loading={complete.isPending}
                  onClick={() => complete.mutate('COMPLETED')}
                >
                  {t('academic.enrollment.complete')}
                </Button>
                <Button
                  variant="ghost"
                  loading={complete.isPending}
                  onClick={() => complete.mutate('WITHDRAWN')}
                >
                  {t('academic.enrollment.withdraw')}
                </Button>
              </>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
